import { CreateBusinessData } from '../../business';
import { BUSINESS_TYPE_OPTIONS } from '../../constants';

interface BusinessInfoSectionProps {
  formData: CreateBusinessData;
  errors: Record<string, string>;
  onChange: (field: keyof CreateBusinessData, value: string | number) => void;
}

/**
 * BusinessInfoSection component for collecting business name, owner, type, and hourly rate
 */
export function BusinessInfoSection({ formData, errors, onChange }: BusinessInfoSectionProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
            Business Name <span className="text-red-500">*</span>
          </label>
          <input
            id="businessName"
            type="text"
            required
            value={formData.businessName}
            onChange={(e) => onChange('businessName', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.businessName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., Sparkle Clean Services"
            aria-invalid={!!errors.businessName}
            aria-describedby={errors.businessName ? 'businessName-error' : undefined}
          />
          {errors.businessName && (
            <p id="businessName-error" className="text-red-600 text-sm mt-1" role="alert">
              {errors.businessName}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700 mb-1">
            Owner Name <span className="text-red-500">*</span>
          </label>
          <input
            id="ownerName"
            type="text"
            required
            value={formData.ownerName}
            onChange={(e) => onChange('ownerName', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.ownerName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., Maria Rodriguez"
            aria-invalid={!!errors.ownerName}
            aria-describedby={errors.ownerName ? 'ownerName-error' : undefined}
          />
          {errors.ownerName && (
            <p id="ownerName-error" className="text-red-600 text-sm mt-1" role="alert">
              {errors.ownerName}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
            Business Type
          </label>
          <select
            id="businessType"
            value={formData.businessType}
            onChange={(e) => onChange('businessType', e.target.value as CreateBusinessData['businessType'])}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {BUSINESS_TYPE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700 mb-1">
            Hourly Rate <span className="text-red-500">*</span>
          </label>
          <input
            id="hourlyRate"
            type="number"
            min="0"
            step="0.01"
            required
            value={formData.hourlyRate || ''}
            onChange={(e) => onChange('hourlyRate', parseFloat(e.target.value) || 0)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.hourlyRate ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="25.00"
            aria-invalid={!!errors.hourlyRate}
            aria-describedby={errors.hourlyRate ? 'hourlyRate-error' : undefined}
          />
          {errors.hourlyRate && (
            <p id="hourlyRate-error" className="text-red-600 text-sm mt-1" role="alert">
              {errors.hourlyRate}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
